// must stay first -- seeds the host page's config before any engine module reads it
import './host-config.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import type { ImageFile } from '@adornis/file-utils/db/images.js';
import { fontReset } from '@adornis/fonts/font-resets.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Bundesland } from '@chancenwerk/enums/enums.js';
import type { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { ProductCHF } from '@chancenwerk/shared/_cms/products/entities/ProductCHF.js';
import { Adresse } from '@chancenwerk/shared/adresse.js';
import '@chancenwerk/client/design/components/cr-checkbox.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-image.js';
import '@chancenwerk/client/design/components/cr-link.js';
import { colors } from '@chancenwerk/client/design/theme.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import type { ChancenSchule } from '@chancenwerk/shared/master-data-management/partner/entities/ChancenSchule.js';
import type { Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import type { School } from '@chancenwerk/shared/master-data-management/partner/entities/School.js';
import { KPFPublicInformationAPI } from '@chancenwerk/shared/public-access/api/KPFPublicInformationAPI.js';
import { PartnerPublicInformationAPI } from '@chancenwerk/shared/public-access/api/PartnerPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { DateTime } from 'luxon';
import { filter, map, switchMap } from 'rxjs';
import fillerImage from '../client/assets/chancenwerk_filler.png';
import './startup.js';

const DEVELOPMENT_ONLY_FILLER = false;
const PRODUCT_BLACKLIST = [ProductCHF._class, 'ProductCAM', 'ProductABS'];

/**
 * Caches the result of a async operation that is related to some key. This can be used to fetch foreign
 * data in a export field and don't re-fetch it in another fields that needs that data.
 * @param fetchFn The function to fetch something by a key
 * @returns Maybe cached value if the fetch function returned something truthy
 */
export function newOperationCache<T>(fetchFn: (id: string) => Promise<Maybe<T>>) {
  const cache: Record<string, Maybe<T>> = {};

  const get = async (id: string): Promise<Maybe<T>> => {
    if (id === '') {
      return null;
    }

    if (cache[id]) {
      return cache[id];
    }

    const elem = await fetchFn(id);
    cache[id] = elem;
    return elem;
  };

  const toString = async (id: string, fmt: (elem: T) => Promise<string>, ifEmpty: string = ''): Promise<string> => {
    const elem = await get(id);
    if (!elem) {
      return ifEmpty;
    }
    return await fmt(elem);
  };

  return { get, toString };
}

// Cache for the KPF operative perosn data
const kpfOperative = newOperationCache<string[]>((id: string) => {
  return KPFPublicInformationAPI.getOperative(id)();
});

const schoolSelector: BaseQLSelectionSet<School | Partner | ChancenSchule> = {
  _class: 1,
  _id: 1,
  name: 1,
  mediaID: 1,
  media: AdornisFile.allFields,
  url: 1,
  hideOnWebsite: 1,
  operative: { name: 1, email: 1 },
  bundesland: 1,
  address: Adresse.allFields,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _class: 1,
  _id: 1,
  schoolID: 1,
  startDatum: 1,
  endDatum: 1,
  produkt: { kuerzel: 1, name: 1 },
  personsOperativ: { vorname: 1, nachname: 1, email: 1 },
  school: { kuerzel: 1 },
};

const NEW_COLORS = { content: '#c9eae7', alt_blue: '#006359' };

@customElement('cw-school-coops-button')
export class SchoolCooperationsButton extends ChemistryLitElement {
  @property({ type: Boolean })
  active: boolean = false;

  override styles() {
    return [
      {
        ':host': {
          display: 'block',
          color: 'white',
          userSelect: 'none',
          cursor: 'pointer',
          '.active': { color: NEW_COLORS.alt_blue, backgroundColor: `${NEW_COLORS.content} !important` },
        },
      },
    ];
  }

  public override render() {
    return html`<cr-text
      class="${this.active ? 'active' : ''}"
      ${css({ padding: '20px 25px', fontSize: '24px', fontWeight: 700, lineHeight: 1 })}
      ><slot></slot
    ></cr-text>`;
  }
}

@customElement('cw-school-coops-header')
export class SchoolCooperationsHeader extends ChemistryLitElement {
  @property({ type: Boolean })
  active: boolean = false;

  override styles() {
    return [
      {
        ':host': {
          backgroundColor: colors.chancen_green_primary,
          borderRadius: '0 20px 0 20px',
          padding: '25px 30px',
          margin: '12px 0',
          'cr-text': { color: 'white', fontSize: '23px', fontWeight: '700' },
        },
      },
    ];
  }

  public override render() {
    return html`<cr-text>
      <slot></slot>
    </cr-text>`;
  }
}

@customElement('cw-school-coops-school')
export class SchoolCooperationsSchool extends ChemistryLitElement {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ type: String })
  image: string = '';

  @property({ type: String })
  schoolName: string = '';

  @property({ type: String })
  schoolLink: string = '';

  @property({ attribute: false })
  kpfs: KPF<Product>[] = [];

  override styles() {
    return [
      {
        ':host': {
          borderBottom: `2px solid ${colors.chancen_green_primary}`,
          margin: '0 0 10px 0',
          padding: '0 0 10px 0',

          'cr-text': {
            lineHeight: 1.2,

            color: `${NEW_COLORS.alt_blue} !important`,
            'cr-link': { color: `${NEW_COLORS.alt_blue} !important` },
          },
        },
      },
    ];
  }

  public override render() {
    return html`<cr-flex ${css(this._size.mobile ? { flexDirection: 'column', alignItems: 'start' } : {})} horizontal>
      <cr-image
        disabled
        .value=${this.image}
        mode="cover"
        chancen-corners
        ${css({
          width: this._size.mobile ? '80%' : '40%',
          padding: this._size.mobile ? '5px 0 10px 0' : '5px 10px',
          marginLeft: this._size.mobile ? '10%' : '',
          height: this._size.mobile ? '164px' : 'auto',
          aspectRatio: '3 / 1',
          maxHeight: '164px',
        })}
      ></cr-image>
      <cr-flex ${css({ width: this._size.mobile ? '100%' : '60%', padding: '20px 10px' })} space="md">
        <cr-text ${css({ fontWeight: 700, fontSize: '20px' })}>
          ${this.schoolLink
            ? html`<cr-link target="_blank" href=${this.schoolLink}> ${this.schoolName} </cr-link>`
            : html`${this.schoolName}`}
        </cr-text>
        ${repeat(
          this.kpfs,
          k => k._id,
          kpf => {
            return html`<cr-text ${css({ fontWeight: 700 })}> ${kpf.produkt?.name ?? 'Unbekanntes Produkt'} </cr-text>
              ${until(
                kpfOperative.get(kpf._id).then(res => {
                  if (!res) return nothing;

                  const [name, email] = res;
                  return html`<cr-text ${css({ fontWeight: 500 })}> ${name} </cr-text>
                    <cr-text ${css({ fontWeight: 500 })}>
                      <cr-link
                        target="_blank"
                        href=${'mailto:' + email}
                        ${css({ wordBreak: 'break-all', lineHeight: 1.2 })}
                      >
                        ${email}
                      </cr-link>
                    </cr-text>`;
                }),
                html`...`,
              )}`;
          },
        )}
      </cr-flex>
    </cr-flex>`;
  }
}

@customElement('cw-school-coops')
export class SchoolCooperations extends ChemistryLitElement {
  @state() private readonly _partners = new RXController(this, PartnerPublicInformationAPI.getAll()(schoolSelector));

  @state() private readonly _kpfs = new RXController(
    this,
    this._partners.observable.pipe(
      switchMap(async partners => {
        if (!partners) {
          return null;
        }
        return KPFPublicInformationAPI.getBySchoolID(partners.filter(Boolean).map(s => s._id) ?? [])(kpfSelector);
      }),
      switchMap(async kpfs => {
        const now = DateTime.now().toMillis();
        return kpfs?.filter(kpf => {
          // * filter active kpfs
          if (!kpf.startDatum || (kpf.startDatum && now < kpf.startDatum.toMillis())) {
            return false;
          }

          if (kpf.endDatum && now > kpf.endDatum.toMillis()) {
            return false;
          }

          // * filter blacklisted classes
          if (PRODUCT_BLACKLIST.includes(kpf.produkt?._class ?? '')) {
            return false;
          }

          return true;
        });
      }),
    ),
  );

  @state() private readonly _activePartners = new RXController(
    this,
    this._kpfs.observable.pipe(
      switchMap(async kpfs => {
        if (!kpfs) {
          return null;
        }

        return this._partners.value?.filter(Boolean).filter(p => {
          if ((p as School).has('hideOnWebsite') && (p as School).hideOnWebsite) {
            return false;
          }

          // * filter kpfs of this school
          const kpfThisSchool = kpfs.filter(kpf => kpf.schoolID === p._id);
          if (kpfThisSchool.length === 0) {
            return false;
          }

          // * filter blacklisted classes
          if (kpfThisSchool.every(kpf => PRODUCT_BLACKLIST.includes(kpf.produkt?._class ?? ''))) {
            return false;
          }

          return kpfs.some(kpf => {
            if (kpf.schoolID !== p._id || (!kpf.startDatum && !kpf.endDatum)) {
              return false;
            }

            return true;
          });
        });
      }),
    ),
  );

  private readonly _states = new RXController(
    this,
    this._activePartners.observable.pipe(
      filter(Boolean),
      map(partners => {
        const states = {};

        // Group by state and then city
        partners.forEach(s => {
          if (!s.bundesland || !s.address?.zipCity) {
            return;
          }

          if (!states[s.bundesland]) {
            states[s.bundesland] = {};
          }

          if (!states[s.bundesland][s.address?.zipCity.city]) {
            states[s.bundesland][s.address?.zipCity.city] = [];
          }

          states[s.bundesland][s.address?.zipCity.city].push(s);
        });

        return states as Record<string, Record<string, School[]>>;
      }),
    ),
  );

  @state() private _selected = Bundesland.BADENWUERTTEMBERG;

  override styles() {
    return [
      fontReset('Montserrat', 2, 400, 1, 14),
      fontReset('Montserrat', 2, 500, 1, 14),
      fontReset('Montserrat', 2, 700, 1, 14),
      {
        ':host': {
          '.main': { minHeight: '640px', borderRadius: '0px 35px 0px 35px' },
          '.sidebar': { backgroundColor: colors.chancen_green_primary, width: '30%', borderRadius: '0px 0px 0px 35px' },
          '.content': {
            backgroundColor: NEW_COLORS.content,
            width: '70%',
            borderRadius: '0px 35px 0px 0px',
            padding: '20px',
            '.header': {
              backgroundColor: colors.chancen_green_primary,
              borderRadius: '0 20px 0 20px',
              color: 'white',
              fontSize: '23px',
              fontWeight: '700',
              padding: '25px 30px',
              margin: '12px 0',
            },
          },
          '@media screen and (max-width: 750px)': {
            '.tablet--fullwidth': { width: '100% !important' },

            '.tablet--flex': { flexDirection: 'column !important', alignItems: 'start !important' },

            '.tablet--no-radius': { borderRadius: '0 !important' },
          },
        },
      },
    ];
  }

  public override render() {
    if (!this._states.value) {
      return html`<cr-flex crossaxis-center space="md" padding="md">
        <cr-loader></cr-loader>
      </cr-flex>`;
    }

    return html`<cr-flex class="main tablet--flex" horizontal>
      <cr-flex class="sidebar tablet--fullwidth tablet--no-radius">
        ${(Object.keys(this._states.value ?? {}) as Bundesland[])
          .sort()
          .map(
            s =>
              html`<cw-school-coops-button ?active="${s === this._selected}" @click=${() => (this._selected = s)}>
                ${s}
              </cw-school-coops-button>`,
          )}
      </cr-flex>
      <cr-flex class="content tablet--fullwidth tablet--no-radius">
        ${Object.keys(this._states.value[this._selected] ?? {})
          .sort()
          .map(city => {
            return html`<cw-school-coops-header>${city}</cw-school-coops-header> ${this._states.value![this._selected]![
                city
              ]!.map(school => {
                return html`<cw-school-coops-school
                  .schoolName=${school.name}
                  .schoolLink=${school.url ?? ''}
                  .image=${this._getImageURL(school.media)}
                  .kpfs=${this._kpfs.value
                    ?.filter(k => k.schoolID === school._id)
                    .filter(k => !PRODUCT_BLACKLIST.includes(k.produkt?._class ?? ''))}
                >
                </cw-school-coops-school>`;
              })}`;
          })}
      </cr-flex>
    </cr-flex>`;
  }

  private _getImageURL(file: Maybe<ImageFile>) {
    if (A.isDevelopment && DEVELOPMENT_ONLY_FILLER) {
      return `${projectConfig.get('RAUM_URL')}${fillerImage}`;
    }

    return file ? file.getServeLink() : `${projectConfig.get('RAUM_URL')}${fillerImage}`;
  }
}
